export type ShotIssueCategory =
  | 'none'
  | 'losing_distance'
  | 'big_right_miss'
  | 'big_left_miss'
  | 'fat_shots'
  | 'thin_or_topped'
  | 'inconsistent_contact'
  | 'driver_only'
  | 'irons_only'
  | 'other';

export type ShotIssueTag =
  // Direction/Curve
  | 'started_left'
  | 'started_right'
  | 'ended_left'
  | 'ended_right'
  | 'straightish'
  // Contact/Flight
  | 'fat'
  | 'thin'
  | 'topped'
  | 'shank'
  | 'toe_contact'
  | 'heel_contact'
  | 'high_ball'
  | 'low_ball'
  | 'losing_distance'
  | 'inconsistent_contact';

/**
 * Derives a coarse shot_issue_category from an array of shot_issue_tags
 * This is used to provide a high-level categorization for filtering and analytics
 */
export function deriveShotIssueCategory(tags: ShotIssueTag[]): ShotIssueCategory | null {
  if (!tags || tags.length === 0) {
    return null;
  }

  // Check for direction-based categories
  if (tags.includes('started_right') || tags.includes('ended_right')) {
    return 'big_right_miss';
  }
  if (tags.includes('started_left') || tags.includes('ended_left')) {
    return 'big_left_miss';
  }

  // Check for contact-based categories
  if (tags.includes('fat')) {
    return 'fat_shots';
  }
  if (tags.includes('thin') || tags.includes('topped')) {
    return 'thin_or_topped';
  }

  // Check for other specific issues
  if (tags.includes('losing_distance')) {
    return 'losing_distance';
  }
  if (tags.includes('inconsistent_contact')) {
    return 'inconsistent_contact';
  }

  // If we have tags but no specific category match, return null
  // The category can remain null - it's optional
  return null;
}

/**
 * Maps ShotIssueCategory to display label
 */
export function getCategoryLabel(category: ShotIssueCategory | null): string {
  if (!category || category === 'none') {
    return 'No specific issue';
  }

  const labels: Record<ShotIssueCategory, string> = {
    none: 'No specific issue',
    losing_distance: 'Losing distance',
    big_right_miss: 'Big right miss (push/slice)',
    big_left_miss: 'Big left miss (pull/hook)',
    fat_shots: 'Too many fat shots',
    thin_or_topped: 'Too many thin/topped shots',
    inconsistent_contact: 'Inconsistent contact',
    driver_only: 'Driver only problem',
    irons_only: 'Irons only problem',
    other: 'Other',
  };

  return labels[category];
}

/**
 * Maps ShotIssueTag to display label
 */
export function getTagLabel(tag: ShotIssueTag): string {
  const labels: Record<ShotIssueTag, string> = {
    started_left: 'Started left',
    started_right: 'Started right',
    ended_left: 'Ended left (hook/draw)',
    ended_right: 'Ended right',
    straightish: 'Straight-ish',
    fat: 'Fat/heavy',
    thin: 'Thin',
    topped: 'Topped',
    shank: 'Shank',
    toe_contact: 'Toe contact',
    heel_contact: 'Heel contact',
    high_ball: 'Ball too high',
    low_ball: 'Ball too low',
    losing_distance: 'Losing distance',
    inconsistent_contact: 'Inconsistent contact',
  };

  return labels[tag];
}

